// src/components/UserMemoryPanel.js
import React, { useEffect, useState, useRef, useLayoutEffect } from "react";
import {
  Box,
  TextField,
  Switch,
  FormControlLabel,
  Button,
  Typography,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Chip,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import { isDarkMode, themeColors } from "../../utils/constants";

/* ---------------- GraphQL ---------------- */

async function fetchUserMemoryProfile() {
  const body = {
    query: `
      mutation UserMemoryProfile {
        userMemoryProfile {
          custom
          settings
          fields
          continuityOwnedFields
        }
      }
    `,
  };
  const res = await fetch("/graphql", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const json = await res.json();
  if (json.errors) throw new Error(json.errors.map(e => e.message).join(", "));
  return json.data.userMemoryProfile;
}

async function updateUserMemoryProfile({ custom, settings, fields, replace = true }) {
  const body = {
    query: `
      mutation UpdateUserMemoryProfile($custom: String, $settings: JSON, $fields: JSON, $replace: Boolean) {
        updateUserMemoryProfile(custom: $custom, settings: $settings, fields: $fields, replace: $replace) {
          custom
          settings
          fields
        }
      }
    `,
    variables: { custom, settings, fields, replace },
  };
  const res = await fetch("/graphql", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const json = await res.json();
  if (json.errors) throw new Error(json.errors.map(e => e.message).join(", "));
  return json.data.updateUserMemoryProfile;
}

async function graphql(query, variables) {
  const res = await fetch("/graphql", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ query, variables }),
  });
  const json = await res.json();
  if (json.errors) throw new Error(json.errors.map((e) => e.message).join(", "));
  return json.data;
}

const MEMORY_ENTRY_FIELDS = `
  subjectRef
  revision
  kind
  summary
  tickers
  authority
  observedAt
  lastSeenAt
`;

async function fetchUserMemoryOverview() {
  const data = await graphql(`
    query UserMemoryOverview {
      userMemoryOverview {
        generation
        pendingConsolidation
        unconsolidated
        reviewingEarlier
        entries { ${MEMORY_ENTRY_FIELDS} }
      }
    }
  `);
  return data.userMemoryOverview;
}

async function correctUserMemoryEntry({ subjectRef, expectedRevision, summary }) {
  const data = await graphql(
    `
      mutation CorrectUserMemoryEntry($subjectRef: String!, $expectedRevision: Int!, $summary: String) {
        correctUserMemoryEntry(subjectRef: $subjectRef, expectedRevision: $expectedRevision, summary: $summary) {
          ${MEMORY_ENTRY_FIELDS}
        }
      }
    `,
    { subjectRef, expectedRevision, summary }
  );
  return data.correctUserMemoryEntry;
}

async function forgetUserMemoryEntry(subjectRef) {
  const data = await graphql(
    `
      mutation ForgetUserMemoryEntry($subjectRef: String!) {
        forgetUserMemoryEntry(subjectRef: $subjectRef) { status }
      }
    `,
    { subjectRef }
  );
  return data.forgetUserMemoryEntry;
}

async function clearUserMemory() {
  const data = await graphql(`
    mutation ClearUserMemory {
      clearUserMemory { status }
    }
  `);
  return data.clearUserMemory;
}

/* ---------------- Fields list: mirror Ruby PROFILE_FIELDS ---------------- */

const PROFILE_FIELD_KEYS = [
  "trading_style",
  "risk_tolerance",
  "trading_products",
  "recent_topics",
  "investment_goals",
  "markets_followed",
  "notable_preferences",
  "time_horizon",
  "preferred_indicators",
  "strategy_type",

  "decision_speed",
  "confidence_level",
  "learning_style",
  "emotional_profile",
  "favorite_assets",
  "typical_trade_duration",
  "pain_points",
  "goal_motivation",
  "communication_tone_preference",
  "risk_management_focus",

  "general_personality_traits",
  "preferred_response_length",
  "user_prompts",
  "active_positions",
  "recent_positions",
  "past_positions",
  "past_topics",
  "general_information",
];

const toLabel = (k) => k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

const DEFAULT_SETTINGS = { charting: { show_charts_by_default: true } };

/* ---------------- Visuals ---------------- */

const textFieldStyles = {
  "& .MuiInputBase-input": { color: themeColors.color },
  "& .MuiFormLabel-root": { color: themeColors.color },
  "& .MuiOutlinedInput-root": {
    color: themeColors.color,
    "& fieldset": { borderColor: themeColors.color },
    "&:hover fieldset": { borderColor: themeColors.color },
    "&.Mui-focused fieldset": { borderColor: themeColors.color },
  },
};

// Spacing for fixed footer and for keeping last inputs visible
const FIXED_BOTTOM_GAP = 82;//58; // Footer height
const SCROLLER_BOTTOM_PAD = 20;

export default function UserMemoryPanel({ setActivePanel }) {
  const [profileVersion, setProfileVersion] = useState(0);
  const [data, setData] = useState({ custom: "", settings: DEFAULT_SETTINGS, fields: {} });
  const [dirty, setDirty] = useState(false);
  const [loading, setLoading] = useState(true);
  const [hasSaved, setHasSaved] = useState(false);
  // Legacy lists continuity memory now owns (positions). They are kept, but
  // not used or rewritten, so they are not offered for editing here.
  const [ownedFields, setOwnedFields] = useState([]);

  const outerRef = useRef(null);
  const fieldsRef = useRef(null);
  const [fieldsHeight, setFieldsHeight] = useState(null);

  // Measure remaining viewport space for the scroller.
  useLayoutEffect(() => {
    const measure = () => {
      if (!fieldsRef.current) return;
      const scrollerTop = fieldsRef.current.getBoundingClientRect().top;
      const vh = window.innerHeight || document.documentElement.clientHeight;
      const available = vh - scrollerTop - FIXED_BOTTOM_GAP - 8;
      setFieldsHeight(Math.max(140, available));
    };

    measure();
    const onResize = () => measure();
    window.addEventListener("resize", onResize);
    window.addEventListener("orientationchange", onResize);

    const ro = new ResizeObserver(measure);
    if (outerRef.current) ro.observe(outerRef.current);
    if (fieldsRef.current) ro.observe(fieldsRef.current);

    const raf = requestAnimationFrame(measure);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("orientationchange", onResize);
      ro.disconnect();
    };
  }, [loading]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const server = await fetchUserMemoryProfile();
        if (!cancelled && server) {
          const normalizedFields = {};
          PROFILE_FIELD_KEYS.forEach((k) => {
            const v = server.fields?.[k];
            normalizedFields[k] = Array.isArray(v)
              ? v
              : (typeof v === "string" && v.length ? v.split("|").map(s => s.trim()) : []);
          });
          setOwnedFields(Array.isArray(server.continuityOwnedFields) ? server.continuityOwnedFields : []);
          setData({
            custom: server.custom ?? "",
            settings: server.settings ?? DEFAULT_SETTINGS,
            fields: normalizedFields,
          });
          setDirty(false);
        }
      } catch (e) {
        console.error("fetchUserMemoryProfile error:", e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [profileVersion]);

  const handleSave = async () => {
    const sanitized = {
      custom: data.custom ?? "",
      settings: data.settings ?? {},
      fields: Object.fromEntries(
        Object.entries(data.fields || {}).map(([k, arr]) => [
          k,
          (Array.isArray(arr) ? arr : String(arr).split("|"))
            .map((s) => (typeof s === "string" ? s.trim() : s))
            .filter((s) => s && String(s).length > 0),
        ])
      ),
    };
    try {
      const saved = await updateUserMemoryProfile({ ...sanitized, replace: true });
      setData(saved);
      setDirty(false);
      setHasSaved(true);
    } catch (e) {
      console.error("updateUserMemoryProfile error:", e);
    }
  };

  // Reload what is saved; unsaved edits are dropped and nothing is written.
  const handleReset = () => {
    setLoading(true);
    setProfileVersion((version) => version + 1);
  };

  const setCustom = (v) => {
    setData((prev) => ({ ...prev, custom: v }));
    setDirty(true);
  };

  const setSetting = (path, v) => {
    const parts = path.split(".");
    setData((prev) => {
      const next = { ...prev, settings: { ...prev.settings } };
      let leaf = next.settings;
      for (let i = 0; i < parts.length - 1; i++) {
        const p = parts[i];
        leaf[p] = leaf[p] ? { ...leaf[p] } : {};
        leaf = leaf[p];
      }
      leaf[parts[parts.length - 1]] = v;
      return next;
    });
    setDirty(true);
  };

  const updateFieldArray = (fieldKey, updater) => {
    setData((prev) => {
      const cur = Array.isArray(prev.fields?.[fieldKey]) ? prev.fields[fieldKey] : [];
      const nextArr = updater(cur);
      return { ...prev, fields: { ...prev.fields, [fieldKey]: nextArr } };
    });
    setDirty(true);
  };

  // Clean, high-contrast neutrals
  const textColor    = isDarkMode ? "#E8EAED" : "#1D1D1F";
  const subTextColor = isDarkMode ? "#9AA0A6" : "#6B7280";
  const cardBg       = isDarkMode ? "#151A20" : "#EBEEF3";
  const fieldBg      = isDarkMode ? "#0F141A" : "#EBEEF3";
  const dividerColor = isDarkMode ? "#2A3240" : "#E5E7EB";
  const itemBg       = isDarkMode ? "#11161C" : "#EBEEF3";
  const itemBorder   = isDarkMode ? "#2A3240" : "transparent";

  return (
    <Box
      ref={outerRef}
      sx={{
        position: "relative", // anchor the fixed footer inside this panel
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        px: 2,
        pt: 0.5,
        mt: 0,
        pb: `${FIXED_BOTTOM_GAP}px`, // make space for footer
      }}
    >
      {/* Header + Actions */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", mb: 1, pb: 1, position: 'relative' }}>
        <Box sx={{ mt: 0, mb: 0.5, position: 'absolute', top: '50px', right: '5px'}}>
          <Typography variant="caption" sx={{ mb: 1, pb: 1, ml: 1, color: subTextColor }}>
            {dirty ? "Unsaved changes" : (hasSaved ? "All changes saved." : "")}
          </Typography>
        </Box>

        <Box sx={{ mt: 0, gap: 0.5}}>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              variant="outlined"
              size="small"
              onClick={handleReset}
              disabled={!dirty || loading}
              sx={{ textTransform: "none" }}
            >
              {/* Discards unsaved edits below; clearing memory is a separate action. */}
              Discard edits
            </Button>
            <Button
              variant="contained"
              size="small"
              onClick={handleSave}
              disabled={!dirty || loading}
              sx={{ textTransform: "none" }}
            >
              Save
            </Button>
          </Box>
        </Box>
      </Box>

      <Divider sx={{ mb: 2, borderColor: dividerColor }} />

      {/* 0) What Incite remembers, with correct and forget */}
      <RememberedItems
        colors={{ subTextColor, itemBg, itemBorder, fieldBg }}
        onCleared={() => setProfileVersion((version) => version + 1)}
      />

      <Divider sx={{ mb: 2, borderColor: dividerColor }} />

      {/* 1) Custom Information / Prompts */}
      <SectionTitle title="Custom Information" />
      <Typography variant="body2" sx={{ mb: 1, color: subTextColor }}>
        Tell Incite about yourself.
      </Typography>

      <TextField
        value={data.custom}
        onChange={(e) => setCustom(e.target.value)}
        multiline
        minRows={4}
        fullWidth
        InputLabelProps={{ shrink: true }}
        sx={{
          ...textFieldStyles,
          mb: 0,
          "& .MuiInputBase-root": {
            backgroundColor: fieldBg,
            borderRadius: 1.25,
            fontSize: 13,
          },
        }}
      />

      <Divider sx={{ my: 2, borderColor: dividerColor }} />

      {/* 2) Settings */}
      <SectionTitle title="Settings" />
      <Typography variant="body2" sx={{ mb: 1, color: subTextColor }}>
        Incite will also adjust autonomously.
      </Typography>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 0, mb: 0 }}>
        <FormControlLabel
          control={
            <Switch
              checked={!!data.settings?.charting?.show_charts_by_default}
              onChange={(e) => setSetting("charting.show_charts_by_default", e.target.checked)}
            />
          }
          label={
            <span style={{ fontSize: 13, color: textColor, fontWeight: 600 }}>
              Show Charts by Default
            </span>
          }
        />
      </Box>

      <Divider sx={{ my: 2, borderColor: dividerColor }} />

      {/* 3) Fields scroller — dynamically sized to remaining viewport */}
      <SectionTitle title="Fields" />
      <Typography variant="body2" sx={{ mb: 1, color: subTextColor }}>
        AI-generated personal preferences and profile info.
        {ownedFields.length > 0 && " Positions you mention are kept under Remembered items, with the date you reported them."}
      </Typography>

      <Box
        ref={fieldsRef}
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1,
          overflowY: "auto",
          height: fieldsHeight ? `${fieldsHeight}px` : "auto",
          pr: 1,
          scrollbarWidth: "thin",
          "&::-webkit-scrollbar": { width: 8 },
          "&::-webkit-scrollbar-thumb": { backgroundColor: "rgba(0,0,0,0.4)", borderRadius: 4 },
          "&::-webkit-scrollbar-track": { backgroundColor: "transparent" },
          pb: `${SCROLLER_BOTTOM_PAD}px`,
          scrollPaddingBottom: `${SCROLLER_BOTTOM_PAD}px`,
        }}
      >
        {PROFILE_FIELD_KEYS.filter((fieldKey) => !ownedFields.includes(fieldKey)).map((fieldKey) => {
          const label = toLabel(fieldKey);
          const items = Array.isArray(data.fields?.[fieldKey]) ? data.fields[fieldKey] : [];

          return (
            <Accordion
              key={fieldKey}
              disableGutters
              elevation={0}
              sx={{
                background: cardBg,
                border: `1px solid ${dividerColor}`,
                borderRadius: "10px !important",
                "&:before": { display: "none" },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{ color: themeColors.color }} />}
                sx={{ "& .MuiAccordionSummary-content": { alignItems: "center", gap: 1 } }}
              >
                <Typography sx={{ color: textColor, fontWeight: 600, fontSize: 13 }}>
                  {label}
                </Typography>
                <Chip
                  label={items.length}
                  size="small"
                  sx={{
                    height: 20,
                    color: textColor,
                    borderColor: dividerColor,
                    borderWidth: 1,
                    borderStyle: "solid",
                    background: "transparent",
                  }}
                />
              </AccordionSummary>

              <AccordionDetails sx={{ pt: 0, pb: 1.5 }}>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  {items.length === 0 && (
                    <Typography variant="caption" sx={{ color: subTextColor, ml: 0.5 }}>
                      (No entries yet)
                    </Typography>
                  )}

                  {items.map((val, idx) => (
                    <Box
                      key={`${fieldKey}-${idx}`}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        backgroundColor: itemBg,
                        border: `1px solid ${itemBorder}`,
                        borderRadius: 1.25,
                        px: 1,
                        py: 0.75,
                        transition: "background-color 120ms ease, border-color 120ms ease",
                        "&:hover": {
                          backgroundColor: isDarkMode ? "#131922" : "#F9FAFB",
                          borderColor: isDarkMode ? "#354057" : "#D1D5DB",
                        },
                      }}
                    >
                      <TextField
                        value={val}
                        onChange={(e) =>
                          updateFieldArray(fieldKey, (arr) => {
                            const next = [...arr];
                            next[idx] = e.target.value;
                            return next;
                          })
                        }
                        size="small"
                        fullWidth
                        placeholder=""
                        InputLabelProps={{ shrink: false }}
                        sx={{
                          ...textFieldStyles,
                          "& .MuiInputBase-root": {
                            backgroundColor: fieldBg,
                            borderRadius: 1,
                            fontSize: 13,
                          },
                        }}
                      />
                      <IconButton
                        aria-label="remove"
                        onClick={() =>
                          updateFieldArray(fieldKey, (arr) => arr.filter((_, i) => i !== idx))
                        }
                        size="small"
                      >
                        <DeleteOutlineIcon sx={{ color: themeColors.color }} />
                      </IconButton>
                    </Box>
                  ))}

                  <Button
                    startIcon={<AddIcon />}
                    variant="text"
                    size="small"
                    onClick={() => updateFieldArray(fieldKey, (arr) => [...arr, ""])}
                    sx={{ alignSelf: "flex-start", textTransform: "none" }}
                  >
                    Add item
                  </Button>
                </Box>
              </AccordionDetails>
            </Accordion>
          );
        })}
      </Box>

      {/* Fixed Privacy Footer */}
      <Box
        className="base-text"
        sx={{
          position: "absolute",//"absolute",
          left: 0,
          bottom: 68,
          width: '100%',
          height: '22px',
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 0, margin: 0,
          color: subTextColor,
          backgroundColor: isDarkMode ? "#222222" : "#E9E9E9",
          fontSize: 11,
        }}
      >
        {/* <Typography variant="caption" sx={{ 
          color: subTextColor, textAlign: "center",
          padding: 0, margin: 0,
          backgroundColor: 'red',
        }}> */}
          Privacy: user information is held strictly private and never disclosed externally.
        {/* </Typography> */}
      </Box>
    </Box>
  );
}

/* ---------------- small helper ---------------- */

const KIND_LABELS = {
  position: "Position",
  preference: "Preference",
  market_workflow: "Workflow",
  user_profile: "About you",
  setting: "Setting",
};

const formatReported = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
};

/* What Incite currently remembers, with the two actions that matter: correct
   what is wrong, and forget what should not be remembered at all. */
function RememberedItems({ colors, onCleared }) {
  const [state, setState] = useState({ status: "loading", overview: null, error: null });
  const [confirmClear, setConfirmClear] = useState(false);
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(null);

  const load = async () => {
    try {
      const overview = await fetchUserMemoryOverview();
      setState({ status: "ready", overview, error: null });
    } catch (e) {
      setState({ status: "error", overview: null, error: e.message });
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const overview = await fetchUserMemoryOverview();
        if (!cancelled) setState({ status: "ready", overview, error: null });
      } catch (e) {
        if (!cancelled) setState({ status: "error", overview: null, error: e.message });
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const saveCorrection = async (entry) => {
    const summary = draft.trim();
    if (!summary || summary === entry.summary) {
      setEditing(null);
      return;
    }
    setBusy(entry.subjectRef);
    try {
      await correctUserMemoryEntry({
        subjectRef: entry.subjectRef,
        expectedRevision: entry.revision,
        summary,
      });
      setEditing(null);
      await load();
    } catch (e) {
      setState((prev) => ({ ...prev, error: e.message }));
    } finally {
      setBusy(null);
    }
  };

  const forget = async (entry) => {
    setBusy(entry.subjectRef);
    try {
      await forgetUserMemoryEntry(entry.subjectRef);
      await load();
    } catch (e) {
      setState((prev) => ({ ...prev, error: e.message }));
    } finally {
      setBusy(null);
    }
  };

  const clearAll = async () => {
    setBusy("__all__");
    try {
      await clearUserMemory();
      setConfirmClear(false);
      await load();
      onCleared?.();
    } catch (e) {
      setState((prev) => ({ ...prev, error: e.message }));
    } finally {
      setBusy(null);
    }
  };

  const overview = state.overview;
  const entries = overview?.entries ?? [];
  const pending = overview?.pendingConsolidation ?? 0;
  const unconsolidated = overview?.unconsolidated ?? 0;
  const reviewingEarlier = overview?.reviewingEarlier ?? 0;

  return (
    <Box sx={{ mb: 2 }}>
      <SectionTitle title="What Incite remembers" />

      {state.status === "loading" && (
        <Typography variant="body2" sx={{ color: colors.subTextColor }}>Loading…</Typography>
      )}

      {state.status === "error" && (
        <Typography variant="body2" sx={{ color: colors.subTextColor }}>
          Memory could not be loaded right now. Your saved information is unchanged.
        </Typography>
      )}

      {state.status === "ready" && entries.length === 0 && pending === 0 && (
        <Typography variant="body2" sx={{ color: colors.subTextColor }}>
          Nothing remembered yet. Incite picks this up from what you tell it in chat.
        </Typography>
      )}

      {state.status === "ready" && entries.map((entry) => {
        const reported = formatReported(entry.observedAt || entry.lastSeenAt);
        const isEditing = editing === entry.subjectRef;
        return (
          <Box
            key={entry.subjectRef}
            sx={{
              backgroundColor: colors.itemBg,
              border: `1px solid ${colors.itemBorder}`,
              borderRadius: 1.25,
              p: 1.25,
              mb: 1,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.5, flexWrap: "wrap" }}>
              <Chip size="small" label={KIND_LABELS[entry.kind] || "Remembered"} />
              {(entry.tickers || []).slice(0, 4).map((ticker) => (
                <Chip key={ticker} size="small" variant="outlined" label={ticker} />
              ))}
              {entry.authority === "user_confirmed" && (
                <Typography variant="caption" sx={{ color: colors.subTextColor }}>corrected by you</Typography>
              )}
              {reported && (
                <Typography variant="caption" sx={{ color: colors.subTextColor, ml: "auto" }}>
                  reported {reported}
                </Typography>
              )}
            </Box>

            {isEditing ? (
              <>
                <TextField
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  multiline
                  minRows={2}
                  fullWidth
                  sx={{
                    ...textFieldStyles,
                    "& .MuiInputBase-root": { backgroundColor: colors.fieldBg, borderRadius: 1.25, fontSize: 13 },
                  }}
                />
                <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
                  <Button size="small" variant="contained" sx={{ textTransform: "none" }}
                    disabled={busy === entry.subjectRef}
                    onClick={() => saveCorrection(entry)}>
                    Save correction
                  </Button>
                  <Button size="small" variant="outlined" sx={{ textTransform: "none" }}
                    onClick={() => setEditing(null)}>
                    Cancel
                  </Button>
                </Box>
              </>
            ) : (
              <>
                <Typography variant="body2" sx={{ color: themeColors.color }}>{entry.summary}</Typography>
                <Box sx={{ display: "flex", gap: 1, mt: 0.75 }}>
                  <Button size="small" sx={{ textTransform: "none" }}
                    disabled={busy === entry.subjectRef}
                    onClick={() => { setEditing(entry.subjectRef); setDraft(entry.summary); }}>
                    Correct
                  </Button>
                  <Button size="small" color="error" sx={{ textTransform: "none" }}
                    disabled={busy === entry.subjectRef}
                    onClick={() => forget(entry)}>
                    Forget this
                  </Button>
                </Box>
              </>
            )}
          </Box>
        );
      })}

      {state.status === "ready" && (pending > 0 || unconsolidated > 0) && (
        <Typography variant="caption" sx={{ color: colors.subTextColor, display: "block", mt: 0.5 }}>
          {pending > 0 && `${pending} recent ${pending === 1 ? "message is" : "messages are"} still being processed. `}
          {unconsolidated > 0 && `${unconsolidated} could not be processed and ${unconsolidated === 1 ? "is" : "are"} kept as your own words. `}
          Incite can still use them in your next question.
        </Typography>
      )}

      {state.status === "ready" && reviewingEarlier > 0 && (
        <Typography variant="caption" sx={{ color: colors.subTextColor, display: "block", mt: 0.5 }}>
          Reviewing your earlier conversations. Anything found there is added with the date you said it and never
          overrides something you told Incite more recently.
        </Typography>
      )}

      <Typography variant="caption" sx={{ color: colors.subTextColor, display: "block", mt: 0.5 }}>
        Forgetting an item removes it from memory. Your conversations are kept and are deleted separately.
      </Typography>

      {state.status === "ready" && (
        confirmClear ? (
          <Box sx={{ mt: 1.25 }}>
            <Typography variant="body2" sx={{ color: themeColors.color, mb: 0.75 }}>
              Clear everything Incite remembers about you? Your conversations, settings, plans and tasks are kept.
            </Typography>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button size="small" variant="contained" color="error" sx={{ textTransform: "none" }}
                disabled={busy === "__all__"} onClick={clearAll}>
                Clear memory
              </Button>
              <Button size="small" variant="outlined" sx={{ textTransform: "none" }}
                onClick={() => setConfirmClear(false)}>
                Cancel
              </Button>
            </Box>
          </Box>
        ) : (
          <Button size="small" color="error" sx={{ textTransform: "none", mt: 0.75, px: 0 }}
            onClick={() => setConfirmClear(true)}>
            Clear all memory
          </Button>
        )
      )}
      {state.status === "ready" && state.error && (
        <Typography variant="caption" sx={{ color: "error.main", display: "block", mt: 0.5 }}>
          {state.error}
        </Typography>
      )}
    </Box>
  );
}

function SectionTitle({ title }) {
  return (
    <Typography
      sx={{
        fontWeight: 600,
        fontSize: 14,
        mb: 0.75,
        color: isDarkMode ? "#E8EAED" : "#1D1D1F",
      }}
    >
      {title}
    </Typography>
  );
}
